/**
 * OutcomePill — compact, theme-tinted status/verdict pill.
 *
 * The dark monograph demos repeatedly hand-roll the same idiom: a small
 * mono-cased label tinted with a single accent color — a soft fill, a slightly
 * stronger border, and the accent as the text color — optionally led by a
 * glyph (✓ / ✗ / ·). `structural-invariant`'s `ReceiptPill` and
 * `cryptographic-immunity`'s `VerdictPill` are two copies of it.
 *
 * This is the shared home for that idiom. It is theme-agnostic: the caller
 * passes the tone `color` (derived from its own panel theme), so the pill works
 * in any demo without the component needing to know app palettes. The tint is
 * composed with the shared `colorWithAlpha` helper, so a pill reads correctly
 * against the dark surfaces these demos render on.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";

export interface OutcomePillProps {
  /** Short status text (e.g. "Accept", "Reject", a receipt kind). */
  readonly label: string;
  /** Accent/tone color the pill is tinted with — usually a panel-theme token. */
  readonly color: string;
  /** Optional leading glyph or node (e.g. ✓ / ✗ / ·). Rendered `aria-hidden`. */
  readonly icon?: React.ReactNode;
  /** Optional hover title (e.g. the reason behind a verdict). */
  readonly title?: string;
  /** Two-digit hex alpha for the soft fill. Defaults to `"1c"` (≈11%). */
  readonly backgroundAlpha?: string;
  /** Two-digit hex alpha for the border. Defaults to `"55"` (≈33%). */
  readonly borderAlpha?: string;
  /** Additional Tailwind class names appended after the defaults. */
  readonly className?: string;
}

const PILL_CLASSES =
  "inline-flex items-center gap-1 rounded-full border border-solid px-2.5 py-0.5 " +
  "font-mono text-[11px] font-bold uppercase tracking-wide leading-tight";

/**
 * `React.memo`-wrapped: panels render many pills inside lists derived from
 * snapshot props that re-render on unrelated state changes.
 */
export const OutcomePill = React.memo(function OutcomePill({
  label,
  color,
  icon,
  title,
  backgroundAlpha = "1c",
  borderAlpha = "55",
  className,
}: OutcomePillProps): React.JSX.Element {
  return (
    <span
      title={title}
      className={cn(PILL_CLASSES, className)}
      style={{
        background: colorWithAlpha(color, backgroundAlpha),
        borderColor: colorWithAlpha(color, borderAlpha),
        color,
      }}
    >
      {icon !== undefined ? <span aria-hidden="true">{icon}</span> : null}
      {label}
    </span>
  );
});
